import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Upload, FileDown, ChevronLeft, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Phase 2 — Member Roster CSV Import.
 *
 * Pure-frontend CSV parser (RFC4180-lite — handles quoted fields and
 * commas inside quotes). Headers are case-insensitive and accept both
 * snake_case and Title Case variants so the pro shop can paste from
 * Excel directly.
 */

const SAMPLE = [
  "First Name,Last Name,Email,Member Number,Membership Category,Handicap,Locker Number",
  "Arnold,Palmer,apalmer@example.com,12345,Resident,4.2,77",
  "Patricia,Sorenstam,psorenstam@example.com,12346,Legacy,+1.0,",
  "Tom,Watson,twatson@example.com,12347,National,6.8,142",
].join("\n");

// Map any acceptable header variant → API field
const HEADER_ALIASES = {
  "first name": "first_name", first_name: "first_name", firstname: "first_name",
  "last name": "last_name", last_name: "last_name", lastname: "last_name", surname: "last_name",
  email: "email", "email address": "email",
  "member number": "member_number", member_number: "member_number", member: "member_number", "member#": "member_number",
  "membership category": "membership_category", membership_category: "membership_category", category: "membership_category",
  handicap: "handicap", hdcp: "handicap",
  "locker number": "locker_number", locker_number: "locker_number", locker: "locker_number",
};

const REQUIRED_FIELDS = ["first_name", "last_name", "email", "member_number"];

function parseCsv(text) {
  // RFC4180-lite parser: handles "quoted, fields" with embedded quotes ""
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field); field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    if (row.some((c) => c.trim() !== "")) rows.push(row);
  }
  return rows;
}

function normalizeHeader(h) {
  // Normalize a CSV header to its API field name. Strategy:
  // 1. lowercase, trim
  // 2. collapse separators (_, -, multiple spaces) to single space
  // 3. tighten whitespace around the `#` sign so "Member #" → "member#"
  // 4. look up via HEADER_ALIASES, with two fallback variants
  const lc = (h || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s*#\s*/g, "#")
    .replace(/\s+/g, " ");
  return (
    HEADER_ALIASES[lc] ||
    HEADER_ALIASES[lc.replace(/ /g, "_")] ||
    HEADER_ALIASES[lc.replace(/\s+/g, "")] ||
    null
  );
}

function rowsFromCsv(text) {
  const parsed = parseCsv(text);
  if (parsed.length === 0) {
    return { rows: [], headers: [], errors: ["The file appears to be empty."] };
  }
  const rawHeaders = parsed[0];
  const headers = rawHeaders.map(normalizeHeader);
  const unknownAt = headers
    .map((h, i) => (h === null ? rawHeaders[i] : null))
    .filter(Boolean);
  const errors = [];
  for (const req of REQUIRED_FIELDS) {
    if (!headers.includes(req)) {
      errors.push(`Missing required column: ${req.replace(/_/g, " ")}`);
    }
  }
  if (unknownAt.length > 0) {
    errors.push(`Unrecognised columns ignored: ${unknownAt.join(", ")}`);
  }
  const rows = [];
  for (let r = 1; r < parsed.length; r++) {
    const obj = {};
    for (let c = 0; c < headers.length; c++) {
      if (headers[c] && parsed[r][c] !== undefined) {
        obj[headers[c]] = parsed[r][c].trim();
      }
    }
    const rowErrors = [];
    for (const req of REQUIRED_FIELDS) {
      if (!obj[req]) rowErrors.push(`missing ${req.replace(/_/g, " ")}`);
    }
    if (obj.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(obj.email)) {
      rowErrors.push("invalid email");
    }
    rows.push({ ...obj, _errors: rowErrors });
  }
  return { rows, headers, errors };
}

export default function AdminMemberImportPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState("csv"); // 'csv' | 'emails'
  const [csvText, setCsvText] = useState("");
  const [parsed, setParsed] = useState({ rows: [], headers: [], errors: [] });
  // "Paste emails only" mode — one email per line/comma/semicolon.
  // The backend creates minimal member shells (name derived from email
  // local-part, member_number = PENDING-xxxxx placeholder). Members fill in
  // their own details after activating via the emailed invite link.
  const [emailsText, setEmailsText] = useState("");
  const parsedEmails = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const piece of emailsText.split(/[\s,;]+/)) {
      const e = piece.trim().toLowerCase();
      if (!e) continue;
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
      if (!valid || seen.has(e)) continue;
      seen.add(e);
      out.push(e);
    }
    return out;
  }, [emailsText]);
  const [sendInvites, setSendInvites] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleText = (text) => {
    setCsvText(text);
    if (text.trim()) setParsed(rowsFromCsv(text));
    else setParsed({ rows: [], headers: [], errors: [] });
  };

  const onFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => handleText(String(e.target?.result || ""));
    reader.readAsText(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  const goodRows = useMemo(
    () => parsed.rows.filter((r) => r._errors.length === 0),
    [parsed.rows],
  );

  const submit = async () => {
    if (mode === "csv" && goodRows.length === 0) {
      setError("Fix the row errors above before importing.");
      return;
    }
    if (mode === "emails" && parsedEmails.length === 0) {
      setError("Paste at least one valid email address.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = mode === "csv"
        ? {
            send_invitations: sendInvites,
            rows: goodRows.map(({ _errors, ...r }) => ({  // eslint-disable-line no-unused-vars
              first_name: r.first_name,
              last_name: r.last_name,
              email: r.email,
              member_number: r.member_number,
              membership_category: r.membership_category || null,
              handicap: r.handicap || null,
              locker_number: r.locker_number || null,
            })),
          }
        : {
            send_invitations: sendInvites,
            emails: parsedEmails,
          };
      const { data } = await api.post("/admin/members/import", payload);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my-crest-club-roster-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-cream min-h-screen" data-testid="admin-import-page">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
        <Link
          to="/admin/members"
          data-testid="import-back"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#1A422D] hover:underline"
        >
          <ChevronLeft size={14} /> Back to Members
        </Link>

        <div className="mt-4">
          <div className="overline">Onboarding</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2">Import Member Roster</h1>
          <p className="text-[#4A4A4A] mt-3 max-w-2xl text-sm">
            Upload a CSV from your club&rsquo;s management system. Each row creates a member account stamped to your club. Existing emails are skipped — safe to re-upload.
          </p>
        </div>

        {result ? (
          <div className="mt-8 surface-card p-8" data-testid="import-result">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={28} className="text-[#1A422D] mt-1" />
              <div>
                <h2 className="font-serif text-2xl">Roster imported</h2>
                <p className="text-[#4A4A4A] mt-1 text-sm">
                  <strong className="text-[#1A422D]">{result.created}</strong> members created
                  {result.skipped > 0 && <> &middot; <strong>{result.skipped}</strong> already existed (skipped)</>}
                  {sendInvites && <> &middot; <strong>{result.invitation_emails_sent}</strong> invitations sent</>}
                </p>
                {result.skipped_emails?.length > 0 && (
                  <details className="mt-3 text-xs">
                    <summary className="cursor-pointer text-[#7A7A7A]">Show skipped emails</summary>
                    <ul className="mt-2 ml-3 list-disc text-[#7A7A7A]">
                      {result.skipped_emails.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  </details>
                )}
                <div className="mt-5 flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/admin/members")}
                    data-testid="import-done"
                    className="btn-primary text-xs"
                  >
                    See members
                  </button>
                  <button
                    type="button"
                    onClick={() => { setResult(null); setCsvText(""); setParsed({ rows: [], headers: [], errors: [] }); }}
                    className="btn-secondary text-xs"
                  >
                    Import another file
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Mode picker — CSV vs. paste-emails-only.
                Defaults to CSV (existing behaviour). The emails-only mode is a
                lower-friction onboarding path for clubs that just want to
                quickly seat their email list and let members fill in their
                own details after activation. */}
            <div className="mt-8 border-b border-[#E5E3D8] flex flex-wrap gap-1 sm:gap-6" data-testid="import-mode-tabs">
              <button
                type="button"
                onClick={() => setMode("csv")}
                data-testid="import-mode-csv"
                className={`px-3 py-3 text-sm uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors ${
                  mode === "csv" ? "border-[#1A422D] text-[#1A422D]" : "border-transparent text-[#7A7A7A] hover:text-[#1A422D]"
                }`}
              >
                Structured CSV
              </button>
              <button
                type="button"
                onClick={() => setMode("emails")}
                data-testid="import-mode-emails"
                className={`px-3 py-3 text-sm uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors ${
                  mode === "emails" ? "border-[#1A422D] text-[#1A422D]" : "border-transparent text-[#7A7A7A] hover:text-[#1A422D]"
                }`}
              >
                Paste Emails Only
              </button>
            </div>

            {mode === "csv" && (
            <div className="contents">
            <section className="mt-8 surface-card p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-serif text-2xl">1 &middot; Choose your file</h2>
                  <p className="text-xs text-[#7A7A7A] mt-1">CSV with these columns: First Name, Last Name, Email, Member Number, Membership Category (opt), Handicap (opt), Locker Number (opt).</p>
                </div>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  data-testid="import-template"
                  className="btn-secondary text-xs inline-flex items-center gap-2"
                >
                  <FileDown size={14} /> Download template
                </button>
              </div>

              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
                data-testid="import-dropzone"
                className={`mt-5 border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
                  dragOver ? "border-[#1A422D] bg-[#E9F0EB]" : "border-[#D9D6CB] bg-[#FAF9F2] hover:border-[#1A422D]"
                }`}
              >
                <Upload size={28} className="mx-auto text-[#1A422D]" />
                <p className="font-serif text-lg mt-3">Drop your CSV here</p>
                <p className="text-xs text-[#7A7A7A] mt-1">or click to choose a file</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
                  className="hidden"
                  data-testid="import-file"
                />
              </div>

              <div className="mt-6">
                <label className="label-elegant">Or paste CSV text</label>
                <textarea
                  value={csvText}
                  onChange={(e) => handleText(e.target.value)}
                  rows={6}
                  placeholder={SAMPLE}
                  data-testid="import-paste"
                  className="input-field w-full mt-1.5 font-mono text-xs"
                />
              </div>
            </section>

            {parsed.rows.length > 0 && (
              <section className="mt-6 surface-card p-6 sm:p-8" data-testid="import-preview">
                <div className="flex items-end justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="font-serif text-2xl">2 &middot; Preview</h2>
                    <p className="text-xs text-[#7A7A7A] mt-1">
                      Parsed <strong>{parsed.rows.length}</strong> rows ·
                      <span className="text-[#1A422D]"> {goodRows.length} ready</span>
                      {parsed.rows.length - goodRows.length > 0 && (
                        <> · <span className="text-[#8a1a1a]">{parsed.rows.length - goodRows.length} need attention</span></>
                      )}
                    </p>
                  </div>
                  <label className="text-xs text-[#1A1A1A] inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendInvites}
                      onChange={(e) => setSendInvites(e.target.checked)}
                      data-testid="import-send-invites"
                      className="accent-[#1A422D]"
                    />
                    Send activation emails to new members
                  </label>
                </div>

                {parsed.errors.length > 0 && (
                  <div className="mt-4 px-4 py-3 text-sm border border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33]">
                    {parsed.errors.map((e) => <div key={e}>{e}</div>)}
                  </div>
                )}

                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F3F1E9] text-xs uppercase tracking-wider text-[#4A4A4A]">
                      <tr>
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Member #</th>
                        <th className="px-3 py-2 text-left">Category</th>
                        <th className="px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.rows.slice(0, 25).map((r, i) => (
                        <tr key={`${r.email || "row"}-${i}`} className={`border-t border-[#E5E3D8] ${r._errors.length > 0 ? "bg-[#fbe9e9]" : ""}`}>
                          <td className="px-3 py-2 text-[#7A7A7A]">{i + 1}</td>
                          <td className="px-3 py-2">{r.first_name} {r.last_name}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.member_number}</td>
                          <td className="px-3 py-2 text-xs">{r.membership_category || "—"}</td>
                          <td className="px-3 py-2">
                            {r._errors.length === 0 ? (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#1A422D]">
                                <CheckCircle2 size={12} /> Ready
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#8a1a1a]" title={r._errors.join(", ")}>
                                <AlertTriangle size={12} /> {r._errors[0]}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsed.rows.length > 25 && (
                    <p className="mt-3 text-xs text-[#7A7A7A]">… and {parsed.rows.length - 25} more.</p>
                  )}
                </div>

                {error && (
                  <div className="mt-4 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
                )}
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting || goodRows.length === 0}
                    data-testid="import-submit"
                    className="btn-primary"
                  >
                    {submitting ? "Importing…" : `Import ${goodRows.length} members`}
                  </button>
                </div>
              </section>
            )}
            </div>
            )}

            {mode === "emails" && (
              <section className="mt-8 surface-card p-6 sm:p-8" data-testid="import-emails-section">
                <h2 className="font-serif text-2xl">Paste a list of member emails</h2>
                <p className="text-xs text-[#7A7A7A] mt-1">
                  One per line, or separated by commas / spaces / semicolons. The system creates
                  a minimal member shell for each email and emails them an activation link — the
                  member sets their own name, password, and member number after they sign in.
                </p>
                <textarea
                  rows={10}
                  value={emailsText}
                  onChange={(e) => setEmailsText(e.target.value)}
                  placeholder={"apalmer@example.com\npsorenstam@example.com\ntwatson@example.com"}
                  data-testid="import-emails-paste"
                  className="input-field w-full mt-4 font-mono text-xs"
                />
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-[#7A7A7A]">
                    {parsedEmails.length === 0
                      ? "No valid emails detected yet."
                      : (
                        <>
                          <span className="text-[#1A422D] font-semibold">{parsedEmails.length}</span> valid email{parsedEmails.length === 1 ? "" : "s"} ready to invite.
                        </>
                      )}
                  </p>
                  <label className="inline-flex items-center gap-2 text-xs text-[#4A4A4A] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendInvites}
                      onChange={(e) => setSendInvites(e.target.checked)}
                      data-testid="import-emails-send"
                      className="accent-[#1A422D] w-4 h-4"
                    />
                    Send activation emails immediately
                  </label>
                </div>
                {error && (
                  <div className="mt-4 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
                )}
                <div className="mt-6 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting || parsedEmails.length === 0}
                    data-testid="import-emails-submit"
                    className="btn-primary"
                  >
                    {submitting
                      ? "Inviting…"
                      : `Invite ${parsedEmails.length} member${parsedEmails.length === 1 ? "" : "s"}`}
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
